import {
  MDSIconButton,
  MDSListBox,
  MDSText,
  MDSTextWeight,
  MDSTextVariant,
  MDSIconKind,
  MDSIcon,
  MDSIconColor,
} from "@/mds";
import { MDSListBoxItemProps } from "@/mds/components/lists/list-box-item";
import { IntegrationsPageContentForm } from "@/pages/integrations/shared/integrations-form";
import { DeleteItemModal } from "@/pages/integrations/shared/integrations-delete-modal";
import { useAppStore } from "@/store";
import { EmailSenderObservable } from "@/store/email-senders/EmailSenderObservable";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import { FC, useState } from "react";
import { IntegrationsFormattingForEmail } from "@/pages/integrations/shared/integrations-formatting-for-email";
import {
  StyledContentListTextContainer,
  StyledHeading,
} from "@/pages/integrations/shared/integrations.styles";

export const IntegrationsSenderItemList: FC = observer(() => {
  const { store } = useAppStore();
  const [itemToDelete, setItemToDelete] = useState<EmailSenderObservable | null>(null);

  const integrationStore = store.emailSenders;

  const { unVerifiedItem, addUnverifiedItem } = integrationStore;

  const { items } = getItems({
    data: integrationStore.items,
    setItemToDelete,
    unVerifiedItem,
    addUnverifiedItem,
  });

  return (
    <Wrapper listHasItems={items.length > 0}>
      <StyledHeading variant={MDSTextVariant.TextXs} weight={MDSTextWeight.SemiBold}>
        Verified Emails
      </StyledHeading>
      <MDSListBox items={items} />
      <DeleteItemModal itemToDelete={itemToDelete} setItemToDelete={setItemToDelete} />

      {!!unVerifiedItem && (
        <IntegrationsPageContentForm hasItems={items.length > 0} unVerifiedItem={unVerifiedItem} />
      )}
      <IntegrationsFormattingForEmail />
    </Wrapper>
  );
});

const getItems = ({
  data,
  setItemToDelete,
  unVerifiedItem,
  addUnverifiedItem,
}: {
  data: EmailSenderObservable[];
  setItemToDelete: (item: EmailSenderObservable) => void;
  unVerifiedItem: EmailSenderObservable | undefined;
  addUnverifiedItem: () => void;
}) => {
  const isFormOpen = !!unVerifiedItem;
  const label = "email";

  const list = data.filter(item => item.id !== unVerifiedItem?.id);

  const items: MDSListBoxItemProps[] = list.map(info => ({
    id: info.id,
    textValue: info.input,
    leftAddon: (
      <UserIconWrapper>
          <MDSIcon kind={MDSIconKind.Envelope} color={MDSIconColor.Secondary} />
      </UserIconWrapper>
    ),
    children: (
      <StyledContentListTextContainer>
        <MDSText variant={MDSTextVariant.TextS}>{info.input}</MDSText>
        <MDSIcon kind={MDSIconKind.CheckCircleSolid} color={MDSIconColor.Positive} />
      </StyledContentListTextContainer>
    ),
    rightAddon: info.canDelete ? (
      <MDSIconButton
        onClick={() => setItemToDelete(info)}
        aria-label={`Delete ${label}`}
        iconKind={MDSIconKind.Trash}
      />
    ) : null,
  }));

  if (!isFormOpen) {
    items.push({
      id: "create-integration-sender",
      textValue: `Connect ${list.length ? "another" : ""}${label}`,
      onAction: addUnverifiedItem,
      leftAddon: (
        <IconWrapper>
          <MDSIcon kind={MDSIconKind.Plus} color={MDSIconColor.Secondary} size={16} />
        </IconWrapper>
      ),
      children: (
        <MDSText variant={MDSTextVariant.TextS}>
          Connect {list.length ? "another " : ""}
          {label}
        </MDSText>
      ),
    });
  }

  return { items };
};

const Wrapper = styled.div<{ listHasItems: boolean }>(({ theme, listHasItems }) => ({
  width: "100%",
  marginTop: theme.spacing["2"],
  padding: 0,
  display: "flex",
  flexDirection: "column",
  gap: listHasItems ? theme.spacing["2"] : theme.spacing["1"],
}));

const UserIconWrapper = styled.div(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing[2],
}));

const IconWrapper = styled.div(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing[2],
  height: 32,
  width: 32,
}));
